"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.transformFlow = transformFlow;
exports.transformHotspotGroup = transformHotspotGroup;
exports.transformJourney = transformJourney;
exports.transformTemplate = transformTemplate;
exports.transformChecklist = transformChecklist;
exports.transformStyle = transformStyle;

var _utils = require("./utils");

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Translates the `flow` schema used in Firebase to the schema the API delivers
 * for consumption by the SDK.
 *
 * Parity should be maintained with the API...
 * https://github.com/appcues/api/blob/master/web/models/content_attributes/modal.ex
 *
 * @param content
 * @returns {*}
 */

function transformFlow(content) {
  return {
    version_id: 1,
    type: "modal",
    redirect_url: null,
    redirect_new_tab: false,
    next_content_id: null,
    name: "Demo Modal",
    id: content.id,
    attributes: {
      pattern_type: content.patternType,
      position: content.position,
      backdrop: content.backdrop,
      is_progress_bar_hidden: content.isProgressBarHidden,
      skippable: content.skippable,
      style: content.style,
      steps: Object.keys(content.steps || {}).map(function (stepChildId, index) {
        var stepChild = content.steps[stepChildId];
        return {
          id: stepChild.id,
          html: stepChild.content,
          next_text: stepChild.nextText,
          prev_text: stepChild.prevText,
          step_number: stepChild.stepNumber || index,
          step_buttons: (stepChild.stepButtons || []).map(function (stepButton) {
            return {
              type: stepButton.type,
              align: stepButton.align,
              text: stepButton.text,
              style: stepButton.style
            };
          }),
          ui_conditions: stepChild.uiConditions,
          next_button_hidden: stepChild.nextButtonHidden,
          prev_button_hidden: stepChild.prevButtonHidden,
          actions_hidden: stepChild.actionsHidden,
          is_button_centered: stepChild.isButtonCentered
        };
      }).sort(function (a, b) {
        return a.step_number - b.step_number;
      })
    }
  };
}

/**
 * Translates the `hotspot-group` schema used in Firebase to the schema the API delivers
 * for consumption by the SDK.
 *
 * Parity should be maintained with the API...
 * https://github.com/appcues/api/blob/master/web/models/content_attributes/hotspots.ex
 *
 * @param content
 * @returns {*}
 */

function transformHotspotGroup(content) {
  return {
    version_id: 1,
    type: "hotspot-group",
    redirect_url: null,
    redirect_new_tab: false,
    next_content_id: null,
    name: "Demo Annotations",
    id: content.id,
    attributes: {
      sequential: content.sequential,
      skippable: content.skippable,
      backdrop: content.backdrop || false,
      backdrop_solid_edge: content.backdropSolidEdge || false,
      backdrop_solid_edge_opacity: content.backdropSolidEdgeOpacity || 0.5,
      beacon_style: content.beaconStyle,
      style: content.style,
      hotspots: Object.keys(content.hotspots || {}).map(function (stepChildId, index) {
        var stepChild = content.hotspots[stepChildId];
        return {
          id: stepChild.id || 0,
          index: stepChild.stepNumber || index,
          selector: stepChild.selector,
          offset_x_percentage: "0.5",
          offset_y_percentage: "0.5",
          backdrop_solid_edge_border_radius: stepChild.backdropSolidEdgeBorderRadius || 0,
          backdrop_solid_edge_x_padding: stepChild.backdropSolidEdgeXPadding || 0,
          backdrop_solid_edge_y_padding: stepChild.backdropSolidEdgeYPadding || 0,
          tooltip_alignment: stepChild.tooltipAlignment || null,
          pattern: stepChild.patternType,
          html: stepChild.content,
          next_text: stepChild.nextText,
          hide_next_button: stepChild.hideNextButton,
          skip_text: stepChild.skipText,
          z_index_override: stepChild.zIndexOverride,
          ui_conditions: stepChild.uiConditions
        };
      }).sort(function (a, b) {
        return a.step_number - b.step_number;
      })
    }
  };
}

/**
 * Translates the `journey` schema used in Firebase to the schema the API delivers
 * for consumption by the SDK
 *
 * @param content
 * @returns {*}
 */

function transformJourney(content) {
  return {
    created_at: content.createdAt,
    created_by: content.createdBy,
    updated_at: content.updatedAt,
    updated_by: content.updatedBy,
    id: content.id,
    name: content.name,
    internal_name: content.internalName,
    state: content.state,
    version_id: content.versionId,
    steps: Object.keys(content.steps || {}).map(function (stepId, index) {
      var step = content.steps[stepId];
      return {
        id: step.id || 0,
        index: step.index || index,
        step_type: step.stepType
      };
    })
  };
}

/**
 * Wraps the stepChild `template` provided as `content` with a step
 * wrapper and applies the proper API transform.
 *
 * @param content
 */

function transformTemplate(content) {
  var contentType = (0, _utils.translateContentType)(content.stepType);
  var stepChildKey = (0, _utils.getStepChildKey)(contentType);

  var wrappedTemplate = _defineProperty({
    name: content.name,
    id: "template",
    createdAt: content.createdAt,
    patternType: content.stepType,
    backdrop: false,
    skippable: true,
    style: content.style
  }, stepChildKey, _defineProperty({}, content.id, Object.assign({}, content.step, {
    id: content.id,
    index: 0,
    stepNumber: 1
  })));

  switch (contentType) {
    case "flows":
      return transformFlow(wrappedTemplate);
    case "hotspot-groups":
      return transformHotspotGroup(wrappedTemplate);
  }
}

function transformChecklist(checklist) {
  return {
    completion_flow_id: checklist.completionFlowId,
    created_at: checklist.createdAt,
    id: checklist.id,
    internal_name: checklist.internalName,
    intro_flow_id: checklist.introFlowId,
    is_sequential: checklist.isSequential,
    items: Object.keys(checklist.items || {}).map(function (itemId) {
      var item = checklist.items[itemId];
      return {
        actions: item.actions,
        conditions: item.conditions,
        depends_on: item.dependsOn,
        id: item.id,
        index: item.index,
        label: item.label,
        parent_item_id: item.parentItemId
      };
    }),
    priority: checklist.priority,
    rule: checklist.rule,
    styles: {
      beacon_background_color: checklist.styles.beaconBackgroundColor,
      beacon_horizontal_offset: checklist.styles.beaconHorizontalOffset,
      beacon_icon: checklist.styles.beaconIcon,
      beacon_text_color: checklist.styles.beaconTextColor,
      beacon_vertical_offset: checklist.styles.beaconVerticalOffset,
      completion_icon: checklist.styles.completionIcon,
      custom_background_color: checklist.styles.customBackgroundColor,
      custom_font_color: checklist.styles.customFontColor,
      custom_hover_state_color: checklist.styles.customHoverStateColor,
      custom_success_image_url: checklist.styles.customSuccessImageUrl,
      font: checklist.styles.font,
      font_url: checklist.styles.fontUrl,
      header_icon: checklist.styles.headerIcon,
      position: checklist.styles.position,
      primary_color: checklist.styles.primaryColor,
      success_color: checklist.styles.successColor,
      success_image_code: checklist.styles.successImageCode,
      type: checklist.styles.type
    },
    text_tokens: {
      beacon_text: checklist.textTokens.beaconText,
      completion_dismiss_text: checklist.textTokens.completionDismissText,
      completion_text: checklist.textTokens.completionText,
      completion_title: checklist.textTokens.completionTitle,
      description_text: checklist.textTokens.descriptionText,
      dismiss_cancel_text: checklist.textTokens.dismissCancelText,
      dismiss_confirm_text: checklist.textTokens.dismissConfirmText,
      dismiss_prompt_text: checklist.textTokens.dismissPromptText,
      dismiss_text: checklist.textTokens.dismissText,
      header_text: checklist.textTokens.headerText
    },
    title: checklist.title,
    type: "checklist",
    updated_at: checklist.updatedAt,
    updated_by: checklist.updatedBy
  };
}

/**
 * Transforms the Firebase representation of a Theme to the API's
 *
 * @param {Object} style
 * @returns {Object}
 */

function transformStyle(style) {
  return {
    globalStyling: style.globalCss,
    typekitId: style.typekitId ? style.typekitId : null,
    globalBeaconColor: style.beaconColor ? style.beaconColor : null,
    globalHotspotAnimation: style.hotspotAnimation ? style.hotspotAnimation : null,
    globalBeaconStyle: style.beaconStyle ? style.beaconStyle : null
  };
}